// The asset page's lineage sections (wave 13, asset-protocol.md section 9c): the forked-from credit and family line,
// the open licence, "use it in your game" actions (add to a project folder, the add command, a prompt for an agent,
// fork), the history timeline, forks, "what changed" and changes offered back. Data comes through StoreBackend; the
// pure helpers are in lineage.ts.

import { ApiError } from "../api.ts";
import { creatorName } from "../components.ts";
import { clear, h, link, toast } from "../dom.ts";
import type { Me } from "../session.ts";
import type { StoreBackend } from "../store-client.ts";
import { assetPath, paramLabel, type AssetDetail, type AssetVersion, type Value } from "../store.ts";
import {
  addCommand,
  agentPrompt,
  diffEmpty,
  diffSummary,
  fileLabel,
  fmtValue,
  forkCommand,
  hunkLines,
  isForkable,
  isSemver,
  lineageErrorText,
  lineCounts,
  manifestLabel,
  nextMinor,
  openLicenceLabel,
  packageFiles,
  paramRows,
  parseRef,
  refOf,
  skippedText,
  type DiffView,
  type FileChange,
  type ForkList,
  type ForkSummary,
  type Lineage,
  type LineageNode,
  type Proposal,
} from "../lineage.ts";

export interface LineageData {
  lineage: Lineage | null;
  forks: ForkList | null;
  /** changes offered to this asset */
  offers: Proposal[] | null;
  /** changes this fork offered to its ancestors */
  offered: Proposal[] | null;
}

/** Everything the sections need, in parallel; a part that fails shows as a quiet note, never breaks the page. */
export async function loadLineage(b: StoreBackend, a: AssetDetail): Promise<LineageData> {
  const soft = <T>(p: Promise<T>): Promise<T | null> => p.catch(() => null);
  const [lineage, forks, offers, offered] = await Promise.all([
    soft(b.lineage(a.id)),
    soft(b.forks(a.id, "best")),
    soft(b.proposals(a.id)),
    a.parent ? soft(b.proposals(a.id, { from: true })) : Promise.resolve(null),
  ]);
  return { lineage, forks, offers, offered };
}

export interface LineageCtx {
  backend: StoreBackend;
  a: AssetDetail;
  me: Me | null;
  data: LineageData;
  /** the configurator's settings that differ from the defaults */
  params: () => Record<string, Value>;
  /** the package files load without a licence (free) */
  openFiles: boolean;
  /** the import line for a three.js game, following the configurator */
  importCode: () => string;
}

const who = (handle: string | null | undefined, name?: string | null): string => creatorName(name ?? null, handle ?? "");
const day = (unix: number): string => (unix ? new Date(unix * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }) : "");
const prettyName = (id: string): string => {
  const n = (id.split("/")[1] ?? id).replace(/-/g, " ");
  return n.charAt(0).toUpperCase() + n.slice(1);
};

/** Titles for every package this page mentions: the lineage, the forks and the asset itself. */
function titles(ctx: LineageCtx): (id: string) => string {
  const t = new Map<string, string>([[ctx.a.id, ctx.a.title]]);
  for (const an of ctx.data.lineage?.ancestors ?? []) if (an.title) t.set(parseRef(an.ref)?.id ?? an.ref, an.title);
  const walk = (ns: LineageNode[]) => ns.forEach((n) => (n.title && t.set(n.id, n.title), walk(n.forks)));
  walk(ctx.data.lineage?.tree ?? []);
  for (const f of ctx.data.forks?.forks ?? []) t.set(f.id, f.title);
  return (id) => t.get(id) ?? prettyName(id);
}

/** "Pumpkin Lantern by oskar" as a link to that version's package. */
function refLink(ctx: LineageCtx, ref: string, withVersion = true): HTMLElement {
  const r = parseRef(ref);
  if (!r) return h("span", null, ref);
  const title = titles(ctx)(r.id);
  return h("span", { class: "ref" }, link(assetPath(r.id), null, title), withVersion ? ` v${r.version}` : null, ` by ${who(r.handle)}`);
}

const isOwner = (ctx: LineageCtx, handle: string): boolean => !!ctx.me?.handle && ctx.me.handle === handle;

// ---- the side: forked from, licence, actions ---------------------------------------------------------------------

/** "Forked from Jack-o'-Lantern v1.2.0 by Banger" on a fork, linking to the parent. */
export function forkedFromLine(ctx: LineageCtx): HTMLElement | null {
  if (!ctx.a.parent) return null;
  return h("p", { class: "forked-from small" }, h("span", { class: "fork-mark", "aria-hidden": "true" }, "⑂"), " Forked from ", refLink(ctx, ctx.a.parent));
}

/** The open licence line for the buy box. */
export function openLicenceLine(a: AssetDetail): HTMLElement {
  const hasCode = !!a.manifest.engines && Object.keys(a.manifest.engines).length > 0;
  const text = openLicenceLabel(a.openLicence, a.price, hasCode);
  return h("p", { class: "small open-licence" }, h("strong", null, "Open licence. "), text, ".");
}

async function copy(text: string, done: string, failed = "Couldn't copy. Select the text instead."): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    toast(done);
    return true;
  } catch {
    toast(failed);
    return false;
  }
}

function codeLine(text: string): HTMLElement {
  return h("div", { class: "code-line" }, h("pre", { class: "code" }, h("code", null, text)), h("button", { type: "button", class: "btn small ghost", onclick: () => copy(text, "Copied") }, "Copy"));
}

type DirHandle = {
  name: string;
  getDirectoryHandle(name: string, o?: { create?: boolean }): Promise<DirHandle>;
  getFileHandle(name: string, o?: { create?: boolean }): Promise<{ createWritable(): Promise<{ write(d: Blob): Promise<void>; close(): Promise<void> }> }>;
};
type Picker = (o?: { mode?: string; id?: string }) => Promise<DirHandle>;

/** Writes the package's files into assets/<creator>/<name>/ inside the folder the player picks. */
async function writePackage(root: DirHandle, ctx: LineageCtx, onFile: (n: number, of: number) => void): Promise<string> {
  const [creator, name] = ctx.a.id.split("/") as [string, string];
  let dir = root;
  for (const part of ["assets", creator, name]) dir = await dir.getDirectoryHandle(part, { create: true });
  const files = packageFiles(ctx.a.manifest.files);
  let n = 0;
  for (const path of files) {
    const r = await fetch(ctx.a.base + path);
    if (!r.ok) throw new Error(`Couldn't download ${path} (${r.status}).`);
    const parts = path.split("/");
    let d = dir;
    for (const p of parts.slice(0, -1)) d = await d.getDirectoryHandle(p, { create: true });
    const w = await (await d.getFileHandle(parts[parts.length - 1]!, { create: true })).createWritable();
    await w.write(await r.blob());
    await w.close();
    onFile(++n, files.length);
  }
  return `assets/${creator}/${name}/`;
}

/** Open and show the folded developer section (terminal, agent, three.js), where every command lives. */
function openDevSection(): void {
  const d = document.querySelector<HTMLDetailsElement>("details.install-details");
  if (!d) return;
  d.open = true;
  d.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
}
const devLink = (text: string) => h("button", { type: "button", class: "linkish", onclick: openDevSection }, text);

/**
 * "Add to project" (or "Get it for your game" where the browser can't write a folder), "Send to my agent" and "Fork",
 * with a box for what happens next. Player-facing: no terminal commands here; they're in the developer fold.
 */
export function useActions(ctx: LineageCtx): HTMLElement {
  const { a } = ctx;
  const out = h("div", { class: "use-out", "aria-live": "polite" });
  const picker = (globalThis as unknown as { showDirectoryPicker?: Picker }).showDirectoryPicker;
  const canWrite = ctx.openFiles && typeof picker === "function";

  const addToProject = async () => {
    if (!canWrite) return openDevSection();
    let root: DirHandle;
    try {
      root = await picker!({ mode: "readwrite", id: "banger-project" });
    } catch {
      return; // closed the picker
    }
    const status = h("p", { class: "small dim" }, "Adding…");
    clear(out, status);
    try {
      const where = await writePackage(root, ctx, (n, of) => (status.textContent = `Adding… ${n} of ${of} files`));
      clear(
        out,
        h("p", { class: "small" }, h("strong", null, "Added. "), `It's in ${where} inside ${root.name}. Paste this into your game:`),
        codeLine(ctx.importCode()),
        h("p", { class: "dim small" }, "Want the version pinned and the store's signature checked? ", devLink("See the developer options"), "."),
      );
    } catch (e) {
      clear(out, h("p", { class: "error small" }, e instanceof Error ? e.message : String(e)), h("p", { class: "small" }, devLink("Other ways to add it"), "."));
    }
  };

  const sendToAgent = async () => {
    const ok = await copy(agentPrompt(a.id, a.version, ctx.params(), a.title), "Copied a prompt for your coding agent", "Couldn't copy here; the prompt is in the developer section.");
    if (ok) clear(out, h("p", { class: "small" }, "Copied, with your settings. Paste it to your coding agent. If it isn't connected to the store yet, ", devLink("here's how"), "."));
    else {
      clear(out, h("p", { class: "small" }, "This browser wouldn't copy. The prompt is in the developer section below, ready to select."));
      openDevSection();
    }
  };

  return h(
    "div",
    { class: "use-actions" },
    h(
      "div",
      { class: "use-row" },
      h("button", { type: "button", class: "btn small", onclick: () => void addToProject() }, canWrite ? "Add to project" : "Get it for your game"),
      h("button", { type: "button", class: "btn small ghost", onclick: () => void sendToAgent() }, "Send to my agent"),
      forkButton(ctx, out),
    ),
    out,
  );
}

function forkButton(ctx: LineageCtx, out: HTMLElement): HTMLElement {
  const { a, me } = ctx;
  if (!isForkable(a.openLicence, a.price)) return h("span", { class: "dim small fork-closed" }, "Not open for forks");
  const open = () => {
    if (!me) {
      const next = encodeURIComponent(location.pathname + location.search);
      return clear(out, h("p", { class: "small" }, "Sign in to fork it. A fork is your own copy to change and publish, with ", who(a.creator.handle, a.creator.name), " credited. ", h("a", { href: `/signin?next=${next}` }, "Sign in")));
    }
    if (!me.handle) {
      return clear(out, h("p", { class: "small" }, "Forks are published under your creator handle. ", link("/dashboard/assets", null, "Claim a handle"), " first, then come back."));
    }
    const name = h("input", { id: "fork-name", value: a.id.split("/")[1] ?? "", autocomplete: "off", spellcheck: "false", pattern: "[a-z0-9][a-z0-9-]{0,63}", required: true });
    const err = h("p", { class: "error small", role: "alert" });
    const form = h(
      "form",
      {
        class: "fork-form",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          const as = `${me.handle}/${(name as HTMLInputElement).value.trim()}`;
          err.textContent = "";
          try {
            const f = await ctx.backend.fork(a.id, as);
            clear(
              out,
              h("p", { class: "small" }, h("strong", null, "Forked. "), `${as} is your draft copy of v${parseRef(f.parent)?.version ?? a.version}, with ${who(a.creator.handle, a.creator.name)} credited.`),
              h("p", { class: "small" }, "Change what you like, then publish it from ", link("/dashboard/assets", null, "your assets"), ". It's checked like any upload before it's listed. To work on its files on your computer, ", devLink("see the developer options"), "."),
            );
          } catch (x) {
            err.textContent = x instanceof ApiError ? lineageErrorText(x.status, x.code, x.message) : x instanceof Error ? x.message : String(x);
          }
        },
      },
      h("label", { for: "fork-name", class: "small" }, "Name your fork"),
      h("div", { class: "fork-name-row" }, h("span", { class: "dim" }, `${me.handle}/`), name, h("button", { type: "submit", class: "btn small" }, "Fork")),
      err,
      a.price > 0 ? h("p", { class: "dim small" }, "Forking a paid asset needs a copy you own, and a fork can't cost less than the original.") : null,
    );
    clear(out, form);
    (name as HTMLInputElement).focus();
  };
  return h("button", { type: "button", class: "btn small ghost", onclick: open }, "Fork");
}

/** The terminal and agent lines for the folded developer section (the only place commands appear). */
export function cliLines(ctx: LineageCtx): HTMLElement {
  return h(
    "div",
    { class: "install-block" },
    h("div", { class: "install-head" }, h("h3", null, "From a terminal")),
    h("p", { class: "dim small" }, "Add this exact version to your project (pinned, signature checked), or fork it to make your own version (the original creator is credited)."),
    codeLine(addCommand(ctx.a.id, ctx.a.version)),
    isForkable(ctx.a.openLicence, ctx.a.price) ? codeLine(forkCommand(ctx.a.id, ctx.me?.handle ?? null)) : null,
    h("div", { class: "install-head" }, h("h3", null, "With your coding agent")),
    h("p", { class: "dim small" }, "Connect the store's tools to your agent once (an MCP server: run ", h("code", null, "thrdm mcp"), "), then paste it this:"),
    h("pre", { class: "code wrap" }, h("code", null, agentPrompt(ctx.a.id, ctx.a.version, ctx.params(), ctx.a.title))),
  );
}

// ---- history -------------------------------------------------------------------------------------------------------

/** Every published version newest first, with its picture, date, merge credit, yank and what changed. */
export function historySection(ctx: LineageCtx): HTMLElement {
  const { a } = ctx;
  const listed = a.versions.filter((v) => v.status === "listed" || v.status === "yanked");
  const latest = listed.find((v) => v.status === "listed")?.version;
  const item = (v: AssetVersion) => {
    const diffBox = h("div", { class: "tl-diff-body" }, h("p", { class: "dim small" }, "Loading…"));
    let loaded = false;
    const details = h(
      "details",
      {
        class: "tl-diff",
        ontoggle: async () => {
          if (loaded || !details.open) return;
          loaded = true;
          try {
            const d = await ctx.backend.diff(a.id, undefined, refOf(a.id, v.version));
            clear(diffBox, d ? diffBody(ctx, d, true) : h("p", { class: "dim small" }, "The first version: nothing before it to compare."));
          } catch (e) {
            loaded = false;
            clear(diffBox, h("p", { class: "error small" }, e instanceof Error ? e.message : String(e)));
          }
        },
      },
      h("summary", null, "What changed"),
      diffBox,
    );
    return h(
      "li",
      { class: `tl-item${v.status === "yanked" ? " yanked" : ""}` },
      h("div", { class: "tl-thumb" }, v.previewUrl ? h("img", { src: v.previewUrl, alt: `v${v.version}`, loading: "lazy" }) : null),
      h(
        "div",
        { class: "tl-body" },
        h(
          "div",
          { class: "tl-head" },
          h("strong", null, `v${v.version}`),
          h("span", { class: "dim small" }, day(v.publishedAt)),
          v.version === latest ? h("span", { class: "badge ok" }, "Latest") : null,
          v.status === "yanked" ? h("span", { class: "badge" }, "Yanked") : null,
        ),
        v.mergedFrom ? h("p", { class: "small tl-credit" }, "Merged from ", refLink(ctx, v.mergedFrom)) : null,
        v.yankReason ? h("p", { class: "dim small" }, v.yankReason) : null,
        details,
      ),
    );
  };
  return h(
    "section",
    { class: "section lineage-section history" },
    h("h2", null, "History"),
    h("p", { class: "dim small" }, `Every version ${who(a.creator.handle, a.creator.name)} has published, newest first. Versions never change once published; a yanked one stays installable for games that pinned it, with a warning.`),
    listed.length ? h("ol", { class: "timeline" }, listed.map(item)) : h("p", { class: "dim" }, "No published versions."),
  );
}

// ---- forks ---------------------------------------------------------------------------------------------------------

function forkCard(f: ForkSummary): HTMLElement {
  const sub = [`by ${who(f.creator.handle, f.creator.name)}`, `${f.versions} ${f.versions === 1 ? "version" : "versions"}`, f.forks ? `${f.forks} ${f.forks === 1 ? "fork" : "forks"}` : null].filter(Boolean).join(" · ");
  return h(
    "li",
    { class: "card asset-card fork-card" },
    link(
      assetPath(f.id),
      { class: "card-link", "aria-label": `${f.title}, ${sub}` },
      h("div", { class: "card-art asset-art" }, f.previewUrl ? h("img", { src: f.previewUrl, alt: "", loading: "lazy" }) : h("span", { class: "card-art-title" }, f.title)),
      h("div", { class: "card-meta" }, h("span", { class: "card-title" }, f.title), h("span", { class: "card-sub" }, sub), f.merged ? h("span", { class: "badge ok merged-badge" }, f.merged === 1 ? "Merged back once" : `Merged back ${f.merged} times`) : null),
    ),
  );
}

/** The family line on a fork: the original → … → this. */
function chain(ctx: LineageCtx): HTMLElement | null {
  const an = ctx.data.lineage?.ancestors ?? [];
  if (!an.length) return null;
  return h(
    "ol",
    { class: "chain", "aria-label": "Where it comes from" },
    an.map((x) => h("li", null, refLink(ctx, x.ref))),
    h("li", { class: "chain-here" }, h("strong", null, ctx.a.title), ` by ${who(ctx.a.creator.handle, ctx.a.creator.name)}`),
  );
}

export function forksSection(ctx: LineageCtx): HTMLElement {
  const { a } = ctx;
  const list = ctx.data.forks;
  const n = list?.count ?? a.forks ?? 0;
  const all = ctx.data.lineage?.descendants ?? n;
  const grid = h("div", null);
  const head = h("p", { class: "lead-small" });
  const draw = (forks: ForkSummary[], more: boolean) => {
    clear(
      grid,
      h("ul", { class: "grid asset-grid fork-grid" }, forks.map(forkCard)),
      more
        ? h(
            "button",
            {
              type: "button",
              class: "btn small ghost see-all",
              onclick: async (e: Event) => {
                (e.currentTarget as HTMLButtonElement).disabled = true;
                try {
                  const r = await ctx.backend.forks(a.id, "recent");
                  head.textContent = `Forked ${r.count} ${r.count === 1 ? "time" : "times"}. Newest first:`;
                  draw(r.forks, false);
                } catch (x) {
                  toast(x instanceof Error ? x.message : String(x));
                }
              },
            },
            `See all ${n}`,
          )
        : null,
    );
  };
  head.textContent = n ? `Forked ${n} ${n === 1 ? "time" : "times"}${all > n ? ` (${all} counting forks of forks)` : ""}.${n > 3 ? " The ones worth a look first:" : ""}` : "";
  if (list && list.forks.length) draw(list.forks.slice(0, 3), n > 3);
  const forkable = isForkable(a.openLicence, a.price);
  return h(
    "section",
    { class: "section lineage-section forks" },
    h("h2", null, "Forks"),
    a.parent ? h("div", { class: "family" }, h("h3", null, "Where it comes from"), chain(ctx) ?? h("p", { class: "small" }, "Forked from ", refLink(ctx, a.parent))) : null,
    n ? head : null,
    grid,
    !list
      ? h("p", { class: "dim small" }, "Forks couldn't load right now.")
      : n
        ? null
        : h("p", { class: "dim" }, forkable ? `No forks yet. Anyone can fork it to make their own version, with ${who(a.creator.handle, a.creator.name)} credited.` : "Its creator hasn't opened it for forks."),
  );
}

// ---- what changed --------------------------------------------------------------------------------------------------

const MAX_LINES = 40;

function fileBlock(f: FileChange, open: boolean): HTMLElement {
  const { added, removed } = lineCounts(f.hunks);
  const note = skippedText(f.skipped);
  const verb = f.change === "added" ? "new" : f.change === "removed" ? "removed" : null;
  const lines = (f.hunks ?? []).flatMap((hk, i) => [...(i ? [{ sign: "@" as const, text: "…", a: null, b: null }] : []), ...hunkLines(hk)]);
  const pre = h("pre", { class: "hunks" });
  const show = (all: boolean) =>
    clear(
      pre,
      (all ? lines : lines.slice(0, MAX_LINES)).map((l) => h("span", { class: `hl ${l.sign === "+" ? "add" : l.sign === "-" ? "del" : l.sign === "@" ? "gap" : "ctx"}` }, h("span", { class: "ln", "aria-hidden": "true" }, l.sign === "@" ? "" : String(l.b ?? l.a ?? "")), h("span", { class: "sg" }, l.sign === "@" ? " " : l.sign), l.text, "\n")),
    );
  show(false);
  const name = fileLabel(f.path) === f.path ? h("code", { class: "path" }, f.path) : h("span", { class: "path", title: f.path }, fileLabel(f.path));
  // nothing to unfold: one line with the reason
  if (!f.hunks?.length) return h("div", { class: "file-diff flat" }, name, verb ? h("span", { class: "badge" }, verb) : null, h("span", { class: "dim small" }, note ?? "No line changes to show."));
  return h(
    "details",
    { class: "file-diff", open: open && !!f.hunks?.length },
    h(
      "summary",
      null,
      fileLabel(f.path) === f.path ? h("code", { class: "path" }, f.path) : h("span", { class: "path", title: f.path }, fileLabel(f.path)),
      verb ? h("span", { class: "badge" }, verb) : null,
      f.hunks?.length ? h("span", { class: "counts small" }, h("span", { class: "add" }, `+${added}`), " ", h("span", { class: "del" }, `−${removed}`)) : null,
      note ? h("span", { class: "dim small" }, note) : null,
    ),
    f.hunks?.length ? h("div", { class: "hunks-wrap" }, pre) : h("p", { class: "dim small" }, note ?? "No line changes to show."),
    lines.length > MAX_LINES ? h("button", { type: "button", class: "btn small ghost", onclick: (e: Event) => (show(true), (e.currentTarget as HTMLElement).remove()) }, `Show all ${lines.length} lines`) : null,
  );
}

/** One diff: settings as a before → after table, other details, then the files with their line changes. */
export function diffBody(ctx: LineageCtx, d: DiffView, compact = false): HTMLElement {
  if (diffEmpty(d)) return h("p", { class: "dim small" }, "Nothing you'd notice changed between these two.");
  const label = (k: string) => paramLabel(k);
  const rows = paramRows(d.params, label);
  const files = d.files.filter((f) => f.path !== "asset.json");
  return h(
    "div",
    { class: "diff-view" },
    h("p", { class: "small diff-sum" }, diffSummary(d)),
    !compact && d.previews.from && d.previews.to && d.previews.from !== d.previews.to
      ? h("div", { class: "diff-shots" }, h("figure", null, h("img", { src: d.previews.from, alt: "" }), h("figcaption", { class: "dim small" }, "Before")), h("figure", null, h("img", { src: d.previews.to, alt: "" }), h("figcaption", { class: "dim small" }, "After")))
      : null,
    rows.length
      ? h(
          "div",
          { class: "table-wrap" },
          h(
            "table",
            { class: "param-diff" },
            h("thead", null, h("tr", null, h("th", null, "Setting"), h("th", null, ""), h("th", null, "Before"), h("th", null, "After"))),
            h(
              "tbody",
              null,
              rows.flatMap((r) =>
                r.cells.map((c, i) =>
                  h(
                    "tr",
                    { class: `pd-${r.change}` },
                    h("td", null, i === 0 ? h("strong", null, r.name) : null),
                    h("td", { class: "dim small" }, c.field),
                    h("td", { class: "before" }, c.before),
                    h("td", { class: "after" }, c.after),
                  ),
                ),
              ),
            ),
          ),
        )
      : null,
    d.manifest.length
      ? h(
          "dl",
          { class: "facts manifest-diff" },
          d.manifest.map((m) => h("div", null, h("dt", null, manifestLabel(m.field)), h("dd", null, h("span", { class: "before" }, fmtValue(m.before)), " → ", h("span", { class: "after" }, fmtValue(m.after))))),
        )
      : null,
    files.length ? h("div", { class: "files" }, h("h3", null, "Files"), files.map((f, i) => fileBlock(f, i === 0 && !compact))) : null,
  );
}

/** Pickers for any two versions (and, on a fork, its ancestors), and the diff between them. */
export function diffSection(ctx: LineageCtx): HTMLElement {
  const { a } = ctx;
  const own = a.versions.filter((v) => v.status === "listed" || v.status === "yanked").map((v) => ({ ref: refOf(a.id, v.version), label: `v${v.version}` }));
  const t = titles(ctx);
  const up = (ctx.data.lineage?.ancestors ?? (a.parent ? [{ ref: a.parent, title: null, handle: null }] : []))
    .slice()
    .reverse()
    .map((x) => ({ ref: x.ref, label: `${t(parseRef(x.ref)?.id ?? x.ref)} v${parseRef(x.ref)?.version ?? ""} (original)` }));
  const options = [...own, ...up];
  const to0 = own[0]?.ref;
  const from0 = a.parent ?? own[1]?.ref;
  const out = h("div", { class: "diff-out" });
  if (!to0 || !from0) {
    return h("section", { class: "section lineage-section what-changed" }, h("h2", null, "What changed"), h("p", { class: "dim" }, "This is the first version, so there's nothing to compare yet."));
  }
  const pick = (id: string, label: string, value: string) =>
    h("label", { class: "diff-pick" }, h("span", { class: "dim small" }, label), h("select", { id, onchange: () => void load() }, options.map((o) => h("option", { value: o.ref, selected: o.ref === value }, o.label))));
  const fromSel = pick("diff-from", "From", from0);
  const toSel = pick("diff-to", "To", to0);
  let seq = 0;
  const load = async () => {
    const from = (fromSel.querySelector("select") as HTMLSelectElement).value;
    const to = (toSel.querySelector("select") as HTMLSelectElement).value;
    const mine = ++seq;
    if (from === to) return clear(out, h("p", { class: "dim small" }, "Pick two different versions."));
    clear(out, h("p", { class: "dim small" }, "Comparing…"));
    try {
      const d = await ctx.backend.diff(a.id, from, to);
      if (mine === seq) clear(out, d ? diffBody(ctx, d) : h("p", { class: "dim small" }, "Nothing to compare."));
    } catch (e) {
      if (mine === seq) clear(out, h("p", { class: "error small" }, e instanceof ApiError ? lineageErrorText(e.status, e.code, e.message) : e instanceof Error ? e.message : String(e)));
    }
  };
  void load();
  return h(
    "section",
    { class: "section lineage-section what-changed" },
    h("h2", null, "What changed"),
    h("p", { class: "dim small" }, a.parent ? "Compare any two versions, or this fork against its original." : "Compare any two versions: its settings, details and code."),
    h("div", { class: "diff-pickers" }, fromSel, h("span", { class: "dim", "aria-hidden": "true" }, "→"), toSel),
    out,
  );
}

// ---- offered back --------------------------------------------------------------------------------------------------

const STATUS: Record<Proposal["status"], { text: string; cls: string }> = {
  open: { text: "Waiting", cls: "warn" },
  merged: { text: "Merged", cls: "ok" },
  declined: { text: "Declined", cls: "" },
  withdrawn: { text: "Withdrawn", cls: "" },
};

export function offersSection(ctx: LineageCtx): HTMLElement | null {
  const { a } = ctx;
  const parentRef = a.parent ? parseRef(a.parent) : null;
  const iOwnFork = !!parentRef && isOwner(ctx, a.creator.handle);
  const offers = h("div", null);
  const offered = h("div", null);
  let toMe = ctx.data.offers ?? [];
  let fromMe = ctx.data.offered ?? [];
  const reload = async () => {
    [toMe, fromMe] = await Promise.all([ctx.backend.proposals(a.id).catch(() => toMe), a.parent ? ctx.backend.proposals(a.id, { from: true }).catch(() => fromMe) : Promise.resolve(fromMe)]);
    draw();
  };

  const card = (p: Proposal, mine: "parent" | "fork" | null, here: "to" | "from") => {
    const st = STATUS[p.status];
    const r = parseRef(p.from);
    const actions = h("div", { class: "offer-actions" });
    const err = h("p", { class: "error small", role: "alert" });
    const act = async (fn: () => Promise<unknown>, done: string) => {
      err.textContent = "";
      try {
        await fn();
        toast(done);
        await reload();
      } catch (x) {
        err.textContent = x instanceof ApiError ? lineageErrorText(x.status, x.code, x.message) : x instanceof Error ? x.message : String(x);
      }
    };
    if (p.status === "open" && mine === "parent") {
      const ver = h("input", { value: nextMinor(a.version), size: 8, "aria-label": "New version number", autocomplete: "off" });
      const note = h("input", { placeholder: "A note for them (optional)", "aria-label": "Note", autocomplete: "off" });
      const changes = h("div", null);
      clear(
        actions,
        h(
          "details",
          {
            class: "offer-diff",
            ontoggle: async (e: Event) => {
              if (!(e.currentTarget as HTMLDetailsElement).open || changes.childElementCount) return;
              clear(changes, h("p", { class: "dim small" }, "Comparing…"));
              const d = await ctx.backend.diff(a.id, refOf(a.id, a.version), p.from).catch((x) => (clear(changes, h("p", { class: "error small" }, String(x instanceof Error ? x.message : x))), null));
              if (d) clear(changes, diffBody(ctx, d, true));
            },
          },
          h("summary", null, "See what it changes"),
          changes,
        ),
        h(
          "form",
          {
            class: "offer-form",
            onsubmit: (e: Event) => {
              e.preventDefault();
              const v = (ver as HTMLInputElement).value.trim();
              if (!isSemver(v)) return void (err.textContent = lineageErrorText(400, "bad_version"));
              void act(() => ctx.backend.decideProposal(a.id, p.id, "merge", { version: v, note: (note as HTMLInputElement).value }), `Merged as v${v}. It's checked like any upload before it's listed.`);
            },
          },
          h("label", { class: "small" }, "Merge as version ", ver),
          note,
          h("div", { class: "offer-buttons" }, h("button", { type: "submit", class: "btn small" }, "Merge"), h("button", { type: "button", class: "btn small ghost", onclick: () => void act(() => ctx.backend.decideProposal(a.id, p.id, "decline", { note: (note as HTMLInputElement).value }), "Declined. They'll see your note.") }, "Decline")),
        ),
      );
    } else if (p.status === "open" && mine === "fork") {
      clear(actions, h("button", { type: "button", class: "btn small ghost", onclick: () => void act(() => ctx.backend.decideProposal(p.package, p.id, "withdraw"), "Withdrawn") }, "Withdraw"));
    }
    return h(
      "li",
      { class: `offer offer-${p.status}` },
      h("div", { class: "offer-head" }, h("strong", null, p.title), h("span", { class: `badge ${st.cls}` }, p.status === "merged" && p.mergedVersion ? `Merged into v${p.mergedVersion}` : st.text)),
      h(
        "p",
        { class: "small dim" },
        here === "to" ? ["From ", r ? refLink(ctx, p.from) : p.from] : ["Offered to ", link(assetPath(p.package), null, titles(ctx)(p.package)), ` by ${who(p.package.split("/")[0])}`, r ? ` (v${r.version})` : null],
        p.createdAt ? ` · ${day(p.createdAt)}` : null,
      ),
      p.message ? h("p", { class: "small offer-msg" }, p.message) : null,
      p.note && p.status !== "open" ? h("p", { class: "small offer-note" }, h("span", { class: "dim" }, `${p.status === "withdrawn" ? who(p.fromHandle) : who(p.package.split("/")[0])}: `), p.note) : null,
      actions,
      err,
    );
  };

  const draw = () => {
    const ownerHere = isOwner(ctx, a.creator.handle);
    const sorted = [...toMe].sort((x, y) => Number(y.status === "open") - Number(x.status === "open") || y.createdAt - x.createdAt);
    clear(
      offers,
      sorted.length
        ? h("ul", { class: "offers" }, sorted.map((p) => card(p, ownerHere ? "parent" : isOwner(ctx, p.fromHandle) ? "fork" : null, "to")))
        : a.parent
          ? null
          : h("p", { class: "dim small" }, "No changes offered back yet."),
    );
    clear(
      offered,
      fromMe.length ? [h("h3", null, "What this fork offered back"), h("ul", { class: "offers" }, fromMe.map((p) => card(p, iOwnFork ? "fork" : null, "from")))] : null,
      iOwnFork && parentRef ? offerForm() : null,
    );
  };

  const offerForm = () => {
    const parentCreator = who(parentRef!.handle);
    const versions = a.versions.filter((v) => v.status === "listed");
    if (!versions.length) return h("p", { class: "dim small" }, `Once a version of your fork is listed, you can offer it to ${parentCreator}.`);
    const sel = h("select", { id: "offer-version" }, versions.map((v, i) => h("option", { value: refOf(a.id, v.version), selected: i === 0 }, `v${v.version}`)));
    const title = h("input", { id: "offer-title", maxlength: 120, required: true, placeholder: "What it changes, in a few words", autocomplete: "off" });
    const msg = h("textarea", { id: "offer-msg", rows: 3, maxlength: 4000, placeholder: "Why it's better (optional)" });
    const err = h("p", { class: "error small", role: "alert" });
    return h(
      "form",
      {
        class: "panel offer-new",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          err.textContent = "";
          try {
            await ctx.backend.propose(parentRef!.id, (sel as HTMLSelectElement).value, (title as HTMLInputElement).value.trim(), (msg as HTMLTextAreaElement).value);
            toast(`Offered to ${parentCreator}`);
            await reload();
          } catch (x) {
            err.textContent = x instanceof ApiError ? lineageErrorText(x.status, x.code, x.message) : x instanceof Error ? x.message : String(x);
          }
        },
      },
      h("h3", null, `Offer this change to ${parentCreator}`),
      h("p", { class: "dim small" }, `If ${parentCreator} likes it, they can merge it into the original, and you're credited there.`),
      h("label", { for: "offer-version", class: "small" }, "Version"),
      sel,
      h("label", { for: "offer-title", class: "small" }, "Title"),
      title,
      h("label", { for: "offer-msg", class: "small" }, "Message"),
      msg,
      h("button", { type: "submit", class: "btn small" }, "Offer this change back"),
      err,
    );
  };

  draw();
  if (!toMe.length && !fromMe.length && !iOwnFork && a.parent) return null;
  return h(
    "section",
    { class: "section lineage-section offered" },
    h("h2", null, "Changes offered back"),
    h("p", { class: "dim small" }, a.parent ? `Improved something? A fork can offer its change back to the original. Merged changes credit the fork.` : `Anyone who forks it can offer their change back to ${who(a.creator.handle, a.creator.name)}. Merged changes credit the fork that made them.`),
    offers,
    offered,
  );
}

/** "Includes changes from Tall Pumpkin by mira" for the details list. */
export function creditsText(ctx: LineageCtx): HTMLElement | null {
  const credits = (ctx.a.manifest.credits as string[] | undefined) ?? [];
  if (!credits.length) return null;
  return h("span", null, credits.map((c, i) => [i ? ", " : null, refLink(ctx, c)]));
}
